import { useEffect, useMemo, useRef, useState } from "react";
import { StatesMachine } from "components/pipeline-components/classes/state-machine/state-machine";
import { MailingTableRow } from "components/mailing-components/mailing-table";
import { DiagnosticPanel } from "./DiagnosticPanel";
import {
  EscolherCenariosModal,
  openEscolherCenariosModal
} from "./EscolherCenariosModal";
import { apiPipeline } from "api/api-pipeline";
import { keyframes } from "@emotion/react";
import {
  Box,
  Text,
  Button,
  VStack,
  HStack,
  Flex,
  Tooltip
} from "@chakra-ui/react";
import { RiRobot2Line } from "react-icons/ri";

const bounce = keyframes`
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-4px); }
`;

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(5px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const blink = keyframes`
  0%, 100% { opacity: 1; }
  50% { opacity: 0; }
`;

const THINKING_STEPS = [
  "Lendo estrutura da base e validando campos obrigatórios...",
  "Identifiquei MARGEM_40 como referência para margem comprometida.",
  "Cruzei RMC e RCC para calcular quantidade de cartões por CPF.",
  "Comparei contratos, taxas e prazos com as regras dos perfis INSS.",
  "Separando oportunidades para Refin com redução + troco, equalização de margem negativa, refin com foco em troco ao cliente e clientes sem oferta minima necessária"
];

// Delays variáveis por etapa para parecer mais natural
const PROCESSING_DELAYS = [900, 1300, 1500, 1100, 1400];
const CHAR_INTERVAL_MS = 18;
const STEP_PAUSE_MS = 300;
const RESULT_DELAY_MS = 700;

type StepStatus = "pending" | "processing" | "typing" | "done";

interface StepState {
  status: StepStatus;
  typed: number;
}

function BotAvatar({ size = "32px" }: { size?: string }) {
  return (
    <Flex
      w={size}
      h={size}
      borderRadius="10px"
      bg="#101936"
      color="white"
      alignItems="center"
      justifyContent="center"
      flexShrink={0}
      fontSize="sm"
    >
      <RiRobot2Line />
    </Flex>
  );
}

export function AnaliseIaEditor({
  selectedStateId,
  pipelineData,
  diagnosticUpdatePipelineData,
  mailingData,
  updatePipelineData
}: {
  selectedStateId?: number;
  pipelineData?: StatesMachine;
  diagnosticUpdatePipelineData?: (
    updater: (draft: StatesMachine) => StatesMachine | void
  ) => void;
  mailingData?: Partial<MailingTableRow>;
  updatePipelineData: (
    updater: (draft: StatesMachine) => StatesMachine | void
  ) => void;
}) {
  const [stage, setStage] = useState<"idle" | "thinking" | "result">(() =>
    selectedStateId != null &&
    pipelineData?.getState(selectedStateId)?.analiseExecuted
      ? "result"
      : "idle"
  );
  const [steps, setSteps] = useState<StepState[]>(() => {
    const isResult =
      selectedStateId != null &&
      pipelineData?.getState(selectedStateId)?.analiseExecuted;
    return THINKING_STEPS.map((text) => ({
      status: (isResult ? "done" : "pending") as StepStatus,
      typed: isResult ? text.length : 0
    }));
  });
  const timers = useRef<ReturnType<typeof setTimeout>[]>([]);
  const runningAnalysisStateIdRef = useRef<number | undefined>(undefined);

  useEffect(() => {
    if (
      stage === "thinking" &&
      selectedStateId === runningAnalysisStateIdRef.current
    ) {
      return;
    }

    const analiseExecuted =
      selectedStateId != null
        ? pipelineData?.getState(selectedStateId)?.analiseExecuted
        : false;
    setStage(analiseExecuted ? "result" : "idle");
    if (analiseExecuted) {
      setSteps(
        THINKING_STEPS.map((text) => ({
          status: "done" as StepStatus,
          typed: text.length
        }))
      );
    }
  }, [selectedStateId, pipelineData, stage]);

  const isStateFinished = useMemo(() => {
    if (selectedStateId == null || !pipelineData || !mailingData?.qtdGerada)
      return false;
    const stats = pipelineData.statusByState.get(selectedStateId);
    if (!stats) return false;
    return stats.finished;
  }, [selectedStateId, pipelineData, mailingData?.qtdGerada]);

  function clearAll() {
    timers.current.forEach((id) => clearTimeout(id));
    timers.current = [];
  }

  function addTimer(id: ReturnType<typeof setTimeout>) {
    timers.current.push(id);
  }

  useEffect(() => {
    if (stage !== "thinking") return;

    clearAll();
    setSteps(THINKING_STEPS.map(() => ({ status: "pending", typed: 0 })));

    function processStep(index: number) {
      if (index >= THINKING_STEPS.length) {
        addTimer(
          setTimeout(() => {
            setStage("result");
            if (selectedStateId != null) {
              updatePipelineData((draft) => {
                const stateAnaliseIA = draft.getState(selectedStateId);
                if (stateAnaliseIA) stateAnaliseIA.analiseExecuted = true;
              });
            }
          }, RESULT_DELAY_MS)
        );
        return;
      }

      setSteps((prev) =>
        prev.map((s, i) => (i === index ? { ...s, status: "processing" } : s))
      );

      addTimer(
        setTimeout(() => {
          setSteps((prev) =>
            prev.map((s, i) =>
              i === index ? { ...s, status: "typing", typed: 0 } : s
            )
          );

          const text = THINKING_STEPS[index];
          let charIndex = 0;

          const typeInterval = setInterval(() => {
            charIndex++;
            setSteps((prev) =>
              prev.map((s, i) => (i === index ? { ...s, typed: charIndex } : s))
            );

            if (charIndex >= text.length) {
              clearInterval(typeInterval);

              setSteps((prev) =>
                prev.map((s, i) =>
                  i === index ? { ...s, status: "done", typed: text.length } : s
                )
              );

              addTimer(setTimeout(() => processStep(index + 1), STEP_PAUSE_MS));
            }
          }, CHAR_INTERVAL_MS);

          addTimer(typeInterval as unknown as ReturnType<typeof setTimeout>);
        }, PROCESSING_DELAYS[index] ?? 1000)
      );
    }

    addTimer(setTimeout(() => processStep(0), 500));

    return clearAll;
  }, [stage, selectedStateId, updatePipelineData]);

  const isProcessing = steps.some((s) => s.status === "processing");

  async function handleExecutarAnalise() {
    try {
      await apiPipeline.put(
        `/pipeline-new/state-ia/${selectedStateId}/execute`
      );
      runningAnalysisStateIdRef.current = selectedStateId;
      setStage("thinking");
    } catch (e) {
      console.error("Erro ao executar análise IA:", e);
    }
  }

  return (
    <Box>
      {stage === "idle" && (
        <>
          <Text color="#90a1b9" fontWeight="600" fontSize="sm" mb={4}>
            Este bloco utiliza inteligência artificial para classificar a base e
            identificar oportunidades de operações INSS, priorizando os leads
            com maior potencial de conversão antes das próximas etapas do
            pipeline.
          </Text>
          {isStateFinished && (
            <Button
              onClick={handleExecutarAnalise}
              borderRadius="10px"
              size="sm"
              mb={4}
            >
              Executar Análise
            </Button>
          )}
        </>
      )}

      {(stage === "thinking" || stage === "result") && (
        <VStack spacing={3} align="stretch">
          {/* Mensagem inicial */}
          <HStack
            align="flex-start"
            spacing={3}
            animation={`${fadeIn} 0.35s ease both`}
          >
            <BotAvatar />
            <Box
              borderRadius="16px"
              borderTopLeftRadius="4px"
              border="1px solid"
              borderColor="gray.200"
              bg="rgba(255, 255, 255, 0.7)"
              px={4}
              py={3}
              shadow="sm"
            >
              <Text fontSize="sm" color="gray.700" lineHeight="tall">
                Estou analisando a base para identificar oportunidades de
                consignado INSS.
              </Text>
            </Box>
          </HStack>

          {/* Etapas */}
          {steps.map((step, i) => {
            if (step.status === "pending" || step.status === "processing")
              return null;

            const fullText = THINKING_STEPS[i];
            const displayText =
              step.status === "typing"
                ? fullText.substring(0, step.typed)
                : fullText;
            const isDone = step.status === "done";

            return (
              <HStack
                key={i}
                align="flex-start"
                spacing={3}
                animation={`${fadeIn} 0.3s ease both`}
              >
                <BotAvatar />
                <Box
                  borderRadius="16px"
                  borderTopLeftRadius="4px"
                  border="1px solid"
                  borderColor="gray.200"
                  bg="rgba(247, 250, 252, 0.6)"
                  px={4}
                  py={3}
                  shadow="sm"
                  flex="1"
                >
                  <HStack align="flex-start" spacing={2}>
                    <Flex
                      mt="3px"
                      w="18px"
                      h="18px"
                      borderRadius="full"
                      bg={isDone ? "green.50" : "blue.50"}
                      color={isDone ? "green.600" : "blue.400"}
                      alignItems="center"
                      justifyContent="center"
                      fontSize="10px"
                      flexShrink={0}
                      transition="background 0.3s, color 0.3s"
                    >
                      {isDone ? "✓" : "…"}
                    </Flex>
                    <Text fontSize="sm" color="gray.500" lineHeight="tall">
                      {displayText}
                      {!isDone && (
                        <Box
                          as="span"
                          display="inline-block"
                          w="1.5px"
                          h="0.85em"
                          bg="gray.400"
                          ml="1px"
                          verticalAlign="text-bottom"
                          animation={`${blink} 0.65s step-end infinite`}
                        />
                      )}
                    </Text>
                  </HStack>
                </Box>
              </HStack>
            );
          })}

          {/* Typing indicator (pontinhos) */}
          {isProcessing && (
            <HStack
              align="flex-start"
              spacing={3}
              animation={`${fadeIn} 0.25s ease both`}
            >
              <BotAvatar />
              <Box
                borderRadius="16px"
                borderTopLeftRadius="4px"
                border="1px solid"
                borderColor="gray.200"
                bg="rgba(255, 255, 255, 0.6)"
                px={4}
                py={3}
                shadow="sm"
              >
                <HStack spacing="6px" align="center" h="20px">
                  {[0, 0.15, 0.3].map((delay) => (
                    <Box
                      key={delay}
                      w="6px"
                      h="6px"
                      bg="gray.400"
                      borderRadius="full"
                      animation={`${bounce} 0.7s ease-in-out ${delay}s infinite`}
                    />
                  ))}
                </HStack>
              </Box>
            </HStack>
          )}

          {/* DiagnosticPanel após resultado */}
          {stage === "result" && (
            <>
              {selectedStateId != null &&
                pipelineData &&
                diagnosticUpdatePipelineData && (
                  <DiagnosticPanel
                    selectedStateId={selectedStateId}
                    pipelineData={pipelineData}
                    operation="ANALISE_IA"
                    updatePipelineData={diagnosticUpdatePipelineData}
                  />
                )}
              <VStack spacing={2} align="stretch" mt={4}>
                <Tooltip
                  label={
                    !pipelineData?.getState(selectedStateId ?? -1)?.paused
                      ? "Etapa já executada"
                      : ""
                  }
                >
                  <Button
                    size="sm"
                    bg="#101936"
                    color="white"
                    borderRadius="10px"
                    _hover={{ bg: "#1a2a50" }}
                    onClick={() =>
                      selectedStateId != null &&
                      pipelineData != null &&
                      openEscolherCenariosModal({
                        stateId: selectedStateId,
                        pipelineData
                      })
                    }
                    isDisabled={
                      !pipelineData?.getState(selectedStateId ?? -1)?.paused
                    }
                  >
                    Escolher cenários para seguir
                  </Button>
                </Tooltip>
                <EscolherCenariosModal
                  diagnosticUpdatePipelineData={diagnosticUpdatePipelineData}
                />
              </VStack>
            </>
          )}
        </VStack>
      )}
    </Box>
  );
}
